<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import ConfirmationModal from '../modal/ConfirmationModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import router from '../../../routes';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    ConfirmationModal,
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'update:show'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    targetConversation: {
      required,
    },
  },
  data() {
    return {
      targetConversation: null,
      isMerging: false,
      isSearching: false,
      searchResults: [],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    currentConversationId() {
      return this.currentChat.id;
    },
  },
  methods: {
    onClose() {
      this.targetConversation = null;
      this.searchResults = [];
      this.v$.$reset();
      this.$emit('close');
    },
    async onConversationSearch(query) {
      if (!query || query.length < 1) {
        this.searchResults = [];
        return;
      }
      const displayId = parseInt(query, 10);
      if (Number.isNaN(displayId)) {
        this.searchResults = [];
        return;
      }

      this.isSearching = true;
      try {
        const response = await ConversationApi.show(displayId);
        if (response.merged || response.data?.id === this.currentChat.id) {
          this.searchResults = [];
        } else if (response.data) {
          this.searchResults = [response.data];
        } else {
          this.searchResults = [];
        }
      } catch (error) {
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      const result = await this.$refs.confirmationModal.showConfirmation();
      if (!result) return;
      this.isMerging = true;
      try {
        await this.$store.dispatch('mergeConversation', {
          conversationId: this.currentChat.id,
          targetConversationId: this.targetConversation.id,
        });
        const path = frontendURL(
          conversationUrl({
            id: this.targetConversation.id,
            accountId: this.targetConversation.account_id,
          })
        );
        this.onClose();
        useAlert(this.$t('MERGE.SUCCESS_MESSAGE'));
        await router.push({ path });
      } catch (error) {
        Sentry.captureException(error);
        useAlert(this.$t('MERGE.ERROR_MESSAGE'));
      } finally {
        this.isMerging = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" @close="onClose">
    <woot-modal-header
      :header-title="$t('MERGE.TITLE')"
      :header-content="$t('MERGE.DESCRIPTION')"
    />

    <form class="flex flex-col gap-4 px-8 py-4" @submit.prevent="onSubmit">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('MERGE.SOURCE.TITLE') }}
        </label>
        <div
          class="flex items-center gap-3 p-3 border rounded-lg border-n-strong bg-n-alpha-2"
        >
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('MERGE.SOURCE.ID_LABEL') }}: #{{ currentConversationId }}
            </span>
            <span class="text-sm truncate text-n-slate-11">
              {{ currentChat.meta.sender.name }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('MERGE.TARGET.TITLE') }}
        </label>
        <div
          class="multiselect-wrap--medium"
          :class="{ error: v$.targetConversation.$error }"
        >
          <multiselect
            v-model="targetConversation"
            :options="searchResults"
            label="id"
            track-by="id"
            :internal-search="false"
            :clear-on-select="false"
            :show-labels="false"
            :placeholder="$t('MERGE.TARGET.PLACEHOLDER')"
            allow-empty
            :loading="isSearching"
            :max-height="200"
            @search-change="onConversationSearch"
          >
            <template #singleLabel="props">
              <div class="flex items-center gap-2">
                <span class="font-medium">#{{ props.option.id }}</span>
                <span class="text-slate-600">-</span>
                <span>{{ props.option.meta?.sender?.name || 'N/A' }}</span>
              </div>
            </template>
            <template #option="props">
              <div class="flex items-center gap-2 py-1">
                <span class="font-medium">#{{ props.option.id }}</span>
                <span class="text-slate-600">-</span>
                <span>{{ props.option.meta?.sender?.name || 'N/A' }}</span>
              </div>
            </template>
            <template #noResult>
              <span>
                {{ $t('MERGE.SEARCH.NO_RESULTS') }}
              </span>
            </template>
          </multiselect>
          <span v-if="v$.targetConversation.$error" class="message">
            {{ $t('MERGE.TARGET.ERROR') }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11">
          {{ $t('MERGE.TARGET.HELP') }}
        </span>
      </div>

      <div
        class="p-3 border rounded-lg bg-n-amber-1 border-n-amber-6 text-n-amber-11"
      >
        <p class="text-sm">
          {{ $t('MERGE.WARNING') }}
        </p>
      </div>

      <div class="flex justify-end gap-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('MERGE.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :is-loading="isMerging"
          :label="$t('MERGE.SUBMIT')"
        />
      </div>
    </form>
    <ConfirmationModal
      ref="confirmationModal"
      :title="$t('MERGE.CONFIRM.TITLE')"
      :description="$t('MERGE.CONFIRM.DESCRIPTION')"
      :confirm-label="$t('MERGE.CONFIRM.YES')"
      :cancel-label="$t('MERGE.CONFIRM.CANCEL')"
    />
  </woot-modal>
</template>

<style lang="scss" scoped>
.error .message {
  @apply mt-1 text-sm text-n-ruby-11;
}

::v-deep {
  .multiselect {
    @apply rounded-md;
  }

  .multiselect__tags {
    @apply h-auto;
  }

  .multiselect__select {
    @apply mt-px mr-1;
  }

  .multiselect__option {
    @apply min-h-0 py-2;
  }
}
</style>
